import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import MessageThreadDialog from "@/components/MessageThreadDialog";
import {
    Plus, Send, Sparkles, Trash2, Pin, Archive, MessagesSquare, Loader2,
    Eye, Bookmark, Briefcase, Mail, Phone, MapPin, ChevronRight, ChevronLeft,
    Star, X, MoreHorizontal, Search as SearchIcon, Keyboard, ArrowRight,
    UserPlus, MessageSquare, Download, ListChecks, Menu,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */
const SCOPE_LABEL = { company: "Company DB", mine: "My candidates", global: "Global pool" };
const scoreTone = (s) => (s >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : s >= 60 ? "bg-amber-100 text-amber-700 border-amber-200"
        : "bg-rose-100 text-rose-700 border-rose-200");

const fmtRelative = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
    return d.toLocaleDateString();
};

const SUGGESTIONS = [
    "Senior backend engineer, 5+ years, Python or Node, Bangalore",
    "Frontend lead with React + TypeScript, willing to relocate to NCR",
    "Designers with SaaS portfolio, ≤ 3 years experience",
    "Data scientist, ML in production, exposure to LLMs",
    "Sales manager with B2B SaaS background, ARR > $1M closed",
];

/* ────────────────────────────────────────────────────────────────────────── */
/*  Sidebar                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */
function Sidebar({ conversations, activeId, onSelect, onNew, onDelete, onTogglePin, onArchive, busy, mobileOpen, onMobileClose }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const qs = q.trim().toLowerCase();
        if (!qs) return conversations;
        return conversations.filter((c) => (c.title || "").toLowerCase().includes(qs));
    }, [q, conversations]);
    const pick = (id) => { onSelect(id); onMobileClose?.(); };
    return (
        <>
            {/* Mobile backdrop */}
            {mobileOpen && (
                <button
                    aria-label="Close sidebar"
                    onClick={onMobileClose}
                    className="fixed inset-0 bg-black/40 z-30 md:hidden"
                    data-testid="ts-sidebar-backdrop"
                />
            )}
            <aside
                className={`bg-[#0a0f1e] text-white flex flex-col h-full shrink-0
                            fixed md:static inset-y-0 left-0 z-40 w-72
                            transition-transform duration-200
                            ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
                data-testid="ts-sidebar"
            >
                <div className="p-3 border-b border-white/10 flex items-center gap-2">
                    <button
                        onClick={() => { onNew(); onMobileClose?.(); }}
                        disabled={busy}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-[#0a0f1e] font-mono text-xs uppercase tracking-widest px-3 py-2 hover:bg-slate-100 disabled:opacity-50"
                        data-testid="ts-new-chat"
                    >
                        <Plus className="w-3.5 h-3.5" /> New search
                    </button>
                    <button
                        onClick={onMobileClose}
                        className="md:hidden p-2 text-white/70 hover:text-white border border-white/10"
                        aria-label="Close"
                        data-testid="ts-sidebar-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-3 pt-3 pb-2 border-b border-white/10">
                    <div className="relative">
                        <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Filter chats"
                            className="w-full bg-white/5 border border-white/10 text-xs placeholder:text-white/30 pl-7 pr-2 py-1.5 outline-none focus:border-white/30"
                            data-testid="ts-filter-chats"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {filtered.length === 0 ? (
                        <div className="text-[11px] text-white/40 px-2 py-6 text-center">No conversations yet</div>
                    ) : filtered.map((c) => (
                        <div
                            key={c.id}
                            className={`group rounded-sm px-2 py-2 cursor-pointer flex items-start gap-2 ${activeId === c.id ? "bg-white/10" : "hover:bg-white/5"}`}
                            onClick={() => pick(c.id)}
                            data-testid={`ts-conv-${c.id}`}
                        >
                            {c.pinned && <Pin className="w-3 h-3 mt-0.5 text-amber-300 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] truncate text-white/90">{c.title || "New search"}</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-0.5 flex items-center gap-2">
                                    <span>{SCOPE_LABEL[c.scope] || "Company DB"}</span>
                                    <span>·</span>
                                    <span>{fmtRelative(c.updated_at)}</span>
                                    {c.candidate_count > 0 && (<><span>·</span><span>{c.candidate_count} results</span></>)}
                                </div>
                            </div>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button onClick={(e) => e.stopPropagation()} className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-1 hover:bg-white/10 rounded" data-testid={`ts-conv-menu-${c.id}`}>
                                        <MoreHorizontal className="w-3.5 h-3.5" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-44 p-1 rounded-none bg-white text-foreground border border-border shadow-md">
                                    <button onClick={() => onTogglePin(c)} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-2">
                                        <Pin className="w-3.5 h-3.5" /> {c.pinned ? "Unpin" : "Pin to top"}
                                    </button>
                                    <button onClick={() => onArchive(c)} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-2">
                                        <Archive className="w-3.5 h-3.5" /> {c.archived ? "Unarchive" : "Archive"}
                                    </button>
                                    <button onClick={() => onDelete(c)} className="w-full text-left text-xs px-2 py-1.5 hover:bg-rose-50 text-rose-700 inline-flex items-center gap-2">
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                </PopoverContent>
                            </Popover>
                        </div>
                    ))}
                </div>
                <div className="p-3 border-t border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/40 hidden sm:flex items-center gap-2">
                    <Keyboard className="w-3 h-3" /> ⌘K new · ⌘↵ send
                </div>
            </aside>
        </>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Candidate card                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
function CandidateCard({ c, onPreview, onActions }) {
    const initials = (c.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div className="border border-border bg-white hover:border-ikb transition-colors p-2.5 sm:p-3 flex flex-col gap-2 group relative" data-testid={`ts-card-${c.id}`}>
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-secondary border border-border flex items-center justify-center text-[11px] font-mono font-bold tracking-wider shrink-0">
                    {initials}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-sm leading-tight truncate">{c.name}</div>
                        {c.from_global && <span className="text-[9px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 bg-violet-50 text-violet-700 border-violet-200">Global</span>}
                    </div>
                    <div className="text-[11px] text-foreground/70 mt-0.5 truncate">{c.current_title || "—"}{c.current_company ? ` @ ${c.current_company}` : ""}</div>
                </div>
                <div className={`text-[10px] font-mono font-bold px-1.5 py-0.5 border ${scoreTone(c.match_score || 0)}`} title="Match score">
                    {c.match_score || 0}
                </div>
            </div>
            <div className="text-[11px] text-foreground/70 flex items-center gap-3 flex-wrap font-mono">
                {typeof c.total_experience_years === "number" && (<span>{c.total_experience_years}y exp</span>)}
                {c.location && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</span>)}
            </div>
            {(c.skills || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {c.skills.slice(0, 5).map((s, i) => (
                        <span key={i} className="text-[10px] font-mono uppercase tracking-wide bg-secondary border border-border px-1.5 py-0.5">{s}</span>
                    ))}
                    {c.skills.length > 5 && <span className="text-[10px] font-mono text-foreground/40">+{c.skills.length - 5}</span>}
                </div>
            )}
            {c.ai_summary && (
                <div className="text-[11px] text-foreground/60 line-clamp-2 leading-snug">{c.ai_summary}</div>
            )}
            {((c.ahead_of_curve || []).length > 0 || (c.dated_stack || []).length > 0) && (
                <div className="flex flex-wrap gap-1 -mt-0.5" data-testid={`ts-trend-signals-${c.id}`}>
                    {(c.ahead_of_curve || []).length > 0 && (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-emerald-500 bg-emerald-50 text-emerald-700 px-1.5 py-0.5"
                            title={`Uses trending/rising skills your company hasn't adopted: ${c.ahead_of_curve.join(", ")}`}
                            data-testid={`ts-ahead-${c.id}`}
                        >
                            ⚡ Ahead of the curve · {c.ahead_of_curve.length}
                        </span>
                    )}
                    {(c.dated_stack || []).length > 0 && (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-rose-500 bg-rose-50 text-rose-700 px-1.5 py-0.5"
                            title={c.dated_stack.map((d) => d.replaced_by ? `${d.skill} → ${d.replaced_by}` : d.skill).join(", ")}
                            data-testid={`ts-dated-${c.id}`}
                        >
                            🕰 Dated stack · {c.dated_stack.length}
                        </span>
                    )}
                </div>
            )}
            <div className="flex items-center gap-1 mt-1 flex-wrap">
                <button onClick={() => onPreview(c)} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`ts-preview-${c.id}`}>
                    <Eye className="w-3 h-3" /> Preview
                </button>
                <button onClick={() => onActions(c, "shortlist")} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`ts-shortlist-${c.id}`}>
                    <Bookmark className="w-3 h-3" /> Shortlist
                </button>
                <button onClick={() => onActions(c, "job")} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`ts-add-job-${c.id}`}>
                    <Briefcase className="w-3 h-3" /> Add to job
                </button>
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`ts-more-${c.id}`}>
                            <MoreHorizontal className="w-3 h-3" /> More
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-48 p-1 rounded-none">
                        <button onClick={() => onActions(c, "message")} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Start thread</button>
                        <button onClick={() => onActions(c, "invite")} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Invite to portal</button>
                        <button onClick={() => onActions(c, "open")} className="w-full text-left text-xs px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5" /> Open detail</button>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  AssistantMessage — assistant turn with candidates + chips                 */
/* ────────────────────────────────────────────────────────────────────────── */
function AssistantMessage({ msg, onChipClick, onPreview, onActions, onBulkShortlist }) {
    return (
        <div className="space-y-3" data-testid={`ts-asst-${msg.id}`}>
            <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-ikb text-white flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
                    <div className="text-[14px] sm:text-[15px] leading-relaxed text-foreground whitespace-pre-wrap">{msg.content}</div>
                    {msg.result_count > 0 && (
                        <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/50 mt-1">
                            {msg.result_count} candidate{msg.result_count === 1 ? "" : "s"} matched
                        </div>
                    )}
                </div>
            </div>
            {(msg.candidates || []).length > 0 && (
                <>
                    <div className="flex items-center justify-between flex-wrap gap-2 ml-9 sm:ml-11">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/50">Top matches</div>
                        <button onClick={() => onBulkShortlist(msg)} className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1" data-testid={`ts-bulk-shortlist-${msg.id}`}>
                            <ListChecks className="w-3 h-3" /> Shortlist all
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3 ml-9 sm:ml-11">
                        {msg.candidates.map((c) => (
                            <CandidateCard key={c.id} c={c} onPreview={onPreview} onActions={onActions} />
                        ))}
                    </div>
                </>
            )}
            {(msg.chips || []).length > 0 && (
                <div className="ml-9 sm:ml-11 pt-1 flex flex-wrap gap-1.5 sm:gap-2">
                    {msg.chips.map((chip, i) => (
                        <button
                            key={i}
                            onClick={() => onChipClick(chip)}
                            className="text-[11px] font-mono border border-border bg-secondary/40 px-2 sm:px-2.5 py-1.5 hover:bg-ikb hover:text-white transition-colors inline-flex items-center gap-1"
                            data-testid={`ts-chip-${msg.id}-${i}`}
                        >
                            {chip} <ArrowRight className="w-3 h-3" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  UserMessage                                                               */
/* ────────────────────────────────────────────────────────────────────────── */
function UserMessage({ msg }) {
    const initials = (msg.author_name || "?").split(/\s|@/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div className="flex items-start gap-2 sm:gap-3 justify-end" data-testid={`ts-user-${msg.id}`}>
            <div className="max-w-[85%] sm:max-w-[80%] bg-ikb text-white px-3 sm:px-4 py-2.5 sm:py-3">
                <div className="text-[14px] sm:text-[15px] whitespace-pre-wrap leading-relaxed">{msg.content}</div>
            </div>
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-secondary border border-border flex items-center justify-center text-[10px] font-mono font-bold shrink-0">{initials}</div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Empty hero                                                                */
/* ────────────────────────────────────────────────────────────────────────── */
function EmptyHero({ onPick }) {
    return (
        <div className="max-w-2xl mx-auto py-8 sm:py-12 px-4 sm:px-6 space-y-5 sm:space-y-6" data-testid="ts-empty-hero">
            <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-ikb text-white">
                    <Sparkles className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl">Find the right people, fast.</h1>
                <p className="text-sm text-muted-foreground">Describe who you're looking for in plain English. I'll narrow the database, suggest follow-ups and you can shortlist or add to a job in one click.</p>
            </div>
            <div className="space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Try one of these</div>
                {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => onPick(s)} className="w-full text-left border border-border bg-white hover:border-ikb px-3 sm:px-4 py-2.5 sm:py-3 text-[13px] sm:text-sm transition-colors" data-testid={`ts-suggestion-${i}`}>
                        {s}
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Shortlist dialog                                                          */
/* ────────────────────────────────────────────────────────────────────────── */
function ShortlistDialog({ open, onClose, candidateIds, conversationId, onDone }) {
    const [lists, setLists] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/hiring-lists").then((r) => setLists(r.data?.items || r.data || [])).catch(() => {});
    }, [open]);

    const submit = async () => {
        if (!selectedId && !newName.trim()) { toast.error("Pick a list or enter a new name"); return; }
        setBusy(true);
        try {
            const payload = { candidate_ids: candidateIds };
            if (selectedId) payload.list_id = selectedId;
            else payload.new_list_name = newName.trim();
            const { data } = await api.post(`/talent-search/conversations/${conversationId}/shortlist`, payload);
            toast.success(`Added ${data.added} candidate${data.added === 1 ? "" : "s"} to list`);
            onDone && onDone(data);
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-lg w-[95vw]" data-testid="ts-shortlist-dialog">
                <DialogHeader>
                    <DialogTitle>Shortlist {candidateIds.length} candidate{candidateIds.length === 1 ? "" : "s"}</DialogTitle>
                    <DialogDescription>Add them to an existing list or create a new one.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {lists.length > 0 && (
                        <div className="space-y-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Existing lists</div>
                            <div className="space-y-1 max-h-48 overflow-y-auto border border-border">
                                {lists.map((l) => (
                                    <label key={l.id} className="flex items-center gap-2 px-3 py-2 hover:bg-secondary cursor-pointer text-sm border-b border-border last:border-b-0">
                                        <input type="radio" name="list" checked={selectedId === l.id} onChange={() => { setSelectedId(l.id); setNewName(""); }} />
                                        <span>{l.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="space-y-1">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Or create a new list</div>
                        <Input value={newName} onChange={(e) => { setNewName(e.target.value); setSelectedId(""); }} placeholder="e.g. Q2 SDE shortlist" className="rounded-none h-10" data-testid="ts-shortlist-newname" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="ts-shortlist-submit">{busy ? "Adding…" : "Add →"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  AddToJob dialog                                                           */
/* ────────────────────────────────────────────────────────────────────────── */
function AddToJobDialog({ open, onClose, candidate }) {
    const [jobs, setJobs] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setPicked(new Set());
        api.get("/jobs", { params: { status: "active" } }).then((r) => setJobs(r.data?.items || r.data || [])).catch(() => {});
    }, [open]);

    const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const submit = async () => {
        if (picked.size === 0) { toast.error("Pick at least one job"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/candidates/${candidate.id}/assign-jobs`, { job_ids: [...picked] });
            const ok = (data.results || []).filter((r) => r.ok).length;
            toast.success(`Assigned to ${ok} job${ok === 1 ? "" : "s"}`);
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!candidate) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-lg w-[95vw]" data-testid="ts-addjob-dialog">
                <DialogHeader>
                    <DialogTitle>Add {candidate.name} to job</DialogTitle>
                    <DialogDescription>The candidate will be auto-scored against each picked job.</DialogDescription>
                </DialogHeader>
                <div className="space-y-1 max-h-72 overflow-y-auto border border-border">
                    {jobs.length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">No active jobs</div>
                    ) : jobs.map((j) => (
                        <label key={j.id} className="flex items-center gap-2 px-3 py-2 hover:bg-secondary cursor-pointer text-sm border-b border-border last:border-b-0">
                            <input type="checkbox" checked={picked.has(j.id)} onChange={() => toggle(j.id)} />
                            <span className="flex-1">{j.title}</span>
                            <span className="text-[10px] font-mono uppercase text-muted-foreground">{j.location || ""}</span>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy || picked.size === 0} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="ts-addjob-submit">{busy ? "Adding…" : `Add to ${picked.size} job${picked.size === 1 ? "" : "s"} →`}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main page                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */
export default function TalentSearch() {
    const nav = useNavigate();
    const { cid: routeCid } = useParams();
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(routeCid || null);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [scope, setScope] = useState("company");
    const [convBusy, setConvBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [shortlistTargets, setShortlistTargets] = useState(null); // {ids:[…]}
    const [addJobFor, setAddJobFor] = useState(null);
    const [messageFor, setMessageFor] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
    const scrollerRef = useRef(null);

    const loadConvs = useCallback(async () => {
        try {
            const { data } = await api.get("/talent-search/conversations");
            setConversations(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);

    useEffect(() => { loadConvs(); }, [loadConvs]);

    // Pick a conversation on mount: route param > most recent > new
    useEffect(() => {
        if (activeId) return;
        if (routeCid) { setActiveId(routeCid); return; }
        // Don't auto-create — show empty hero until user clicks "New search" or picks one.
    }, [routeCid, activeId]);

    const loadActive = useCallback(async (id) => {
        if (!id) { setActive(null); setMessages([]); return; }
        try {
            const { data } = await api.get(`/talent-search/conversations/${id}`);
            setActive(data.conversation);
            setMessages(data.messages || []);
            setScope(data.conversation?.scope || "company");
        } catch (err) {
            toast.error(formatApiError(err));
            setActiveId(null);
        }
    }, []);

    useEffect(() => { loadActive(activeId); }, [activeId, loadActive]);

    useEffect(() => {
        // Auto-scroll on new messages
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }, [messages.length, sending]);

    const newConversation = async (initialPrompt) => {
        setConvBusy(true);
        try {
            const { data } = await api.post("/talent-search/conversations", { title: "New search", scope });
            await loadConvs();
            setActiveId(data.id);
            nav(`/talent-search/${data.id}`, { replace: true });
            if (initialPrompt) {
                // Wait one tick so activeId state propagates, then post
                setTimeout(() => send(data.id, initialPrompt), 50);
            }
            return data.id;
        } catch (err) {
            toast.error(formatApiError(err));
            return null;
        } finally { setConvBusy(false); }
    };

    const send = async (convId, content) => {
        const text = (content || input).trim();
        if (!text) return;
        if (!convId) {
            // No active conversation — create one then re-enter
            const id = await newConversation(text);
            if (!id) return;
            setInput("");
            return;
        }
        // Optimistically push a user bubble
        const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: text, ts: new Date().toISOString(), author_name: "You", _optimistic: true };
        setMessages((m) => [...m, tempUser]);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post(`/talent-search/conversations/${convId}/messages`, { content: text, scope });
            // Replace temp + push assistant
            setMessages((m) => [...m.filter((x) => !x._optimistic), data.user_msg, data.assistant_msg]);
            await Promise.all([loadConvs(), loadActive(convId)]);
        } catch (err) {
            setMessages((m) => m.filter((x) => !x._optimistic));
            toast.error(formatApiError(err));
        } finally { setSending(false); }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
            e.preventDefault();
            send(activeId);
        }
    };

    // Global shortcuts
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                newConversation();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [scope]);

    const onChip = (text) => send(activeId, text);

    const onActions = async (c, kind) => {
        if (kind === "shortlist") setShortlistTargets({ ids: [c.id] });
        else if (kind === "job") setAddJobFor(c);
        else if (kind === "message") setMessageFor(c);
        else if (kind === "open") window.open(`/candidates/${c.id}`, "_blank");
        else if (kind === "invite") {
            try {
                const { data } = await api.post(`/candidates/${c.id}/invite-to-portal`, {});
                if (data?.claim_url) {
                    await navigator.clipboard?.writeText(data.claim_url).catch(() => {});
                    toast.success("Invite link copied to clipboard");
                } else {
                    toast.success("Invite sent");
                }
            } catch (err) { toast.error(formatApiError(err)); }
        }
    };

    const onBulkShortlist = (msg) => {
        const ids = (msg.candidates || []).map((c) => c.id);
        if (ids.length === 0) { toast.error("No candidates to shortlist"); return; }
        setShortlistTargets({ ids });
    };

    const togglePin = async (c) => {
        try { await api.patch(`/talent-search/conversations/${c.id}`, { pinned: !c.pinned }); loadConvs(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const archive = async (c) => {
        try { await api.patch(`/talent-search/conversations/${c.id}`, { archived: !c.archived }); loadConvs(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const remove = async (c) => {
        if (!window.confirm(`Delete "${c.title}"?`)) return;
        try {
            await api.delete(`/talent-search/conversations/${c.id}`);
            if (activeId === c.id) { setActiveId(null); setActive(null); setMessages([]); nav("/talent-search", { replace: true }); }
            loadConvs();
            toast.success("Deleted");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="flex h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] bg-white" data-testid="ts-root">
            <Sidebar
                conversations={conversations}
                activeId={activeId}
                onSelect={(id) => { setActiveId(id); nav(`/talent-search/${id}`, { replace: true }); }}
                onNew={() => newConversation()}
                onDelete={remove}
                onTogglePin={togglePin}
                onArchive={archive}
                busy={convBusy}
                mobileOpen={sidebarOpen}
                onMobileClose={() => setSidebarOpen(false)}
            />

            <main className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <div className="border-b border-border px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="md:hidden p-2 -ml-1 border border-border hover:bg-secondary shrink-0"
                            aria-label="Open conversations"
                            data-testid="ts-open-sidebar"
                        >
                            <Menu className="w-4 h-4" />
                        </button>
                        <div className="min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">AI Talent Search</div>
                            <div className="font-display font-bold text-sm sm:text-base truncate" data-testid="ts-active-title">{active?.title || "Start a conversation"}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={scope}
                            onChange={async (e) => {
                                const v = e.target.value;
                                setScope(v);
                                if (activeId) {
                                    try { await api.patch(`/talent-search/conversations/${activeId}`, { scope: v }); loadConvs(); }
                                    catch { /* silent */ }
                                }
                            }}
                            className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 outline-none"
                            data-testid="ts-scope"
                        >
                            <option value="company">Company DB</option>
                            <option value="mine">My candidates</option>
                            <option value="global">+ Global pool</option>
                        </select>
                    </div>
                </div>

                {/* Messages */}
                <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-secondary/30">
                    {!activeId ? (
                        <EmptyHero onPick={(p) => newConversation(p)} />
                    ) : messages.length === 0 ? (
                        <EmptyHero onPick={(p) => send(activeId, p)} />
                    ) : (
                        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-5 sm:space-y-6">
                            {messages.map((m) => (
                                m.role === "assistant" ? (
                                    <AssistantMessage
                                        key={m.id}
                                        msg={m}
                                        onChipClick={onChip}
                                        onPreview={(c) => setPreview(c)}
                                        onActions={onActions}
                                        onBulkShortlist={onBulkShortlist}
                                    />
                                ) : (
                                    <UserMessage key={m.id} msg={m} />
                                )
                            ))}
                            {sending && (
                                <div className="flex items-start gap-2 sm:gap-3" data-testid="ts-typing">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-ikb text-white flex items-center justify-center"><Sparkles className="w-4 h-4 animate-pulse" /></div>
                                    <div className="pt-2 text-sm text-muted-foreground inline-flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Searching the database…
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Composer */}
                <div className="border-t border-border bg-white p-2 sm:p-4 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
                    <div className="max-w-4xl mx-auto flex items-end gap-2">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKey}
                            placeholder="Describe the candidate you're looking for…"
                            rows={1}
                            className="flex-1 border border-border bg-white px-3 py-2.5 text-[15px] outline-none focus:border-ikb resize-none max-h-40"
                            style={{ minHeight: 44 }}
                            data-testid="ts-composer"
                        />
                        <Button
                            onClick={() => send(activeId)}
                            disabled={sending || !input.trim()}
                            className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11 px-3 sm:px-5 shrink-0"
                            aria-label="Send"
                            data-testid="ts-send"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Send</span></>)}
                        </Button>
                    </div>
                </div>
            </main>

            {/* Modals */}
            {preview && (
                <CandidateProfileDialog
                    open={!!preview}
                    onClose={() => setPreview(null)}
                    candidateId={preview.id}
                />
            )}
            {shortlistTargets && activeId && (
                <ShortlistDialog
                    open={true}
                    onClose={() => setShortlistTargets(null)}
                    candidateIds={shortlistTargets.ids}
                    conversationId={activeId}
                />
            )}
            {addJobFor && (
                <AddToJobDialog
                    open={true}
                    onClose={() => setAddJobFor(null)}
                    candidate={addJobFor}
                />
            )}
            {messageFor && (
                <MessageThreadDialog
                    candidate={messageFor}
                    onClose={() => setMessageFor(null)}
                />
            )}
        </div>
    );
}
